/**
 * Guided Daily Flow Coach
 * Narrates the Brain → Priorities → Capacity journey and surfaces feasibility signals.
 */
export class GuidedDailyFlow {
  constructor(container, dataManager, options = {}) {
    this.container = container;
    this.data = dataManager;
    this.modules = options.modules || {};
    this.onOpenPlanner = options.onOpenPlanner || (() => {});
    this.analytics = options.analytics || null;
    this.unsubscribeFns = [];
    this.cachedContext = null;

    if (!this.container || !this.data) {
      console.warn('[GuidedDailyFlow] Missing container or data manager');
      return;
    }

    this.initialize();
  }

  initialize() {
    this.render();
    this.subscribeToData();
  }

  subscribeToData() {
    const subscribe = (key, handler) => {
      if (typeof this.data.subscribe !== 'function') return;
      try {
        const unsubscribe = this.data.subscribe(key, handler);
        if (typeof unsubscribe === 'function') {
          this.unsubscribeFns.push(unsubscribe);
        }
      } catch (error) {
        console.warn('[GuidedDailyFlow] Failed to subscribe to', key, error);
      }
    };

    subscribe('simpleBrainDumpItems', () => this.render());
    subscribe('unifiedTasks', () => this.render());
    subscribe('enoughTasks', () => this.render());
    subscribe('calendar_events', () => this.render());
    subscribe('integrationState', () => this.render());
    subscribe('valuesAnchors', () => this.render());
  }

  getBrainItems() {
    const items = this.data.get('simpleBrainDumpItems', []);
    return Array.isArray(items) ? items : [];
  }

  getUnifiedTasks() {
    const tasks = this.data.get('unifiedTasks', []);
    return Array.isArray(tasks) ? tasks : [];
  }

  getCapacityTasks() {
    const tasks = this.data.get('enoughTasks', []);
    return Array.isArray(tasks) ? tasks : [];
  }

  getCalendarEvents() {
    const events = this.data.get('calendar_events', []);
    return Array.isArray(events) ? events : [];
  }

  getIntegrationState() {
    const state = this.data.get('integrationState', {});
    return state && typeof state === 'object' ? state : {};
  }

  getAnchors() {
    const anchors = this.data.get('valuesAnchors', {});
    return anchors && typeof anchors === 'object' ? anchors : {};
  }

  calculateFlowContext() {
    const brainItems = this.getBrainItems();
    const unifiedTasks = this.getUnifiedTasks();
    const capacityTasks = this.getCapacityTasks();
    const calendarEvents = this.getCalendarEvents();
    const integration = this.getIntegrationState();
    const calendarState = integration.calendar || {};
    const anchors = this.getAnchors();

    const unsortedCount = brainItems.filter((item) => !item.priority || item.priority === 'unsorted').length;
    const highPriorityCount = brainItems.filter((item) => item.priority === 'high').length;
    const totalBrainItems = brainItems.length;

    const activeCapacityTasks = capacityTasks.filter((task) => !task.completed);

    const steps = [
      {
        id: 'brain',
        title: 'Brain Space',
        emoji: '🧠',
        status: totalBrainItems > 0 ? 'complete' : 'active',
        summary:
          totalBrainItems > 0
            ? `${totalBrainItems} captured${unsortedCount > 0 ? ` · ${unsortedCount} to sort` : ''}`
            : 'Empty — brain dump what’s swirling',
        hint:
          totalBrainItems > 0
            ? unsortedCount > 0
              ? 'Move anything still unsorted into a priority lane.'
              : 'Looks centered. Add anything new that’s buzzing.'
            : 'Dump everything on your mind to get a clean slate.',
      },
      {
        id: 'priorities',
        title: 'Priorities',
        emoji: '🎯',
        status: highPriorityCount > 0 ? 'complete' : totalBrainItems > 0 ? 'active' : 'locked',
        summary:
          highPriorityCount > 0
            ? `${highPriorityCount} high-focus item${highPriorityCount === 1 ? '' : 's'}`
            : 'Choose your top focus items',
        hint:
          highPriorityCount > 0
            ? 'Great! You can pull these directly into your plan.'
            : totalBrainItems > 0
              ? 'Drag the “must happen” items into the High lane.'
              : 'Capture a brain dump first to spot today’s priorities.',
      },
      {
        id: 'capacity',
        title: 'Capacity',
        emoji: '⚡',
        status: activeCapacityTasks.length > 0 ? 'complete' : highPriorityCount > 0 ? 'active' : 'locked',
        summary:
          activeCapacityTasks.length > 0
            ? `${activeCapacityTasks.length} task${activeCapacityTasks.length === 1 ? '' : 's'} planned`
            : 'Balance your plan with actual energy',
        hint:
          activeCapacityTasks.length > 0
            ? 'Check the energy rings to make sure you still have buffer.'
            : highPriorityCount > 0
              ? 'Pull the top items into your planner so they’re energy-checked.'
              : 'Pick your priorities first — then we’ll balance energy.',
      },
    ];

    const topThreeData = this.calculateTopThreeFeasibility(activeCapacityTasks, calendarEvents);

    return {
      steps,
      topThree: topThreeData.topThree,
      feasibility: topThreeData.feasibility,
      calendarSummary: topThreeData.calendarSummary,
      anchors,
      calendarState,
    };
  }

  calculateTopThreeFeasibility(capacityTasks, calendarEvents) {
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    const activeTasks = Array.from(capacityTasks || []).filter((task) => !task.completed);

    // Determine top three by priority, then recency
    const sorted = activeTasks.sort((a, b) => {
      const priorityDiff = (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
      if (priorityDiff !== 0) return priorityDiff;
      return (a.created || 0) - (b.created || 0);
    });

    const topThree = sorted.slice(0, 3);

    const topThreeDurationMinutes = topThree.reduce(
      (total, task) => total + Math.max(15, Number(task.duration) || 30),
      0
    );

    const topThreeCapacity = topThree.reduce((total, task) => total + this.estimateCapacityPercent(task), 0);

    const totalCapacityUsed = activeTasks.reduce((total, task) => total + this.estimateCapacityPercent(task), 0);

    const calendarInsights = this.calculateCalendarSummary(calendarEvents);
    const focusBudgetMinutes = calendarInsights.availableFocusMinutes;

    let status = 'idle';
    let message = 'Pick up to three focus anchors for today.';
    const bufferMinutes = focusBudgetMinutes - topThreeDurationMinutes;

    if (topThree.length === 0) {
      status = 'idle';
      message = 'Pick up to three focus anchors for today.';
    } else if (bufferMinutes < -60 || topThreeCapacity > 110) {
      status = 'overload';
      message =
        'Your Top Three will likely overrun today. Trim or split something so you still have breathing room.';
    } else if (bufferMinutes < 0 || topThreeCapacity > 95) {
      status = 'tight';
      message =
        'The plan is tight — consider lightening one task or shortening a calendar block to create margin.';
    } else if (topThreeCapacity < 60 && bufferMinutes > 60) {
      status = 'good';
      message = 'Nice pacing. You have wiggle room to respond to surprises or add a small win.';
    } else {
      status = 'steady';
      message = 'Your Top Three fits today. Check energy after lunch to see if anything needs swapping.';
    }

    // Calendar conflict hint
    if (calendarInsights.conflictWarning) {
      message += ' Heads up: calendar holds a big chunk of your day.';
    }

    return {
      topThree: topThree.map((task) => ({
        id: task.id,
        text: task.text || task.title || 'Untitled task',
        durationMinutes: Math.max(15, Number(task.duration) || 30),
        capacityPercent: this.estimateCapacityPercent(task),
        priority: task.priority || 'medium',
      })),
      feasibility: {
        status,
        message,
        totalCapacity: Math.round(topThreeCapacity),
        totalDurationMinutes: topThreeDurationMinutes,
        bufferMinutes,
      },
      calendarSummary: calendarInsights,
    };
  }

  calculateCalendarSummary(events) {
    if (!Array.isArray(events) || events.length === 0) {
      return {
        totalEvents: 0,
        focusMinutes: 360,
        busyMinutes: 0,
        availableFocusMinutes: 360,
        conflictWarning: false,
      };
    }

    const todayKey = this.toDateKey(new Date());
    const todaysEvents = events.filter((event) => this.toDateKey(event.start) === todayKey);
    const baselineFocusMinutes = 360; // 6 hours of intentional focus time

    if (todaysEvents.length === 0) {
      return {
        totalEvents: 0,
        focusMinutes: baselineFocusMinutes,
        busyMinutes: 0,
        availableFocusMinutes: baselineFocusMinutes,
        conflictWarning: false,
      };
    }

    const busyMinutes = todaysEvents.reduce((total, event) => total + this.getEventDurationMinutes(event), 0);
    const availableFocusMinutes = Math.max(0, baselineFocusMinutes - busyMinutes);

    return {
      totalEvents: todaysEvents.length,
      focusMinutes: baselineFocusMinutes,
      busyMinutes,
      availableFocusMinutes,
      conflictWarning: availableFocusMinutes < baselineFocusMinutes * 0.4,
    };
  }

  estimateCapacityPercent(task) {
    if (!task || typeof task !== 'object') return 0;
    if (typeof task.adjustedCapacity === 'number') return task.adjustedCapacity;
    if (typeof task.calculatedCapacity === 'number') return task.calculatedCapacity;
    if (typeof task.energyWeight === 'number') return task.energyWeight;

    const durationMinutes = Math.max(15, Number(task.duration) || 30);
    return Math.min(100, Math.round(durationMinutes / 3)); // Rough heuristic used elsewhere
  }

  getEventDurationMinutes(event) {
    if (!event) return 0;

    const start = this.toDate(event.start);
    const end = this.toDate(event.end);

    if (event.allDay || (!event.start && !event.end)) {
      return 480; // assume all-day blocks most of the day
    }

    if (!start || !end) {
      return 0;
    }

    const diff = (end.getTime() - start.getTime()) / 60000;
    if (!Number.isFinite(diff) || diff <= 0) {
      return 0;
    }

    return Math.min(diff, 480);
  }

  toDate(value) {
    if (!value) return null;
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  toDateKey(value) {
    const date = this.toDate(value);
    if (!date) return null;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;
  }

  render() {
    if (!this.container) return;
    const previous = this.cachedContext;
    const context = this.calculateFlowContext();
    this.reportContextMetrics(previous, context);
    this.cachedContext = context;

    const { steps, topThree, feasibility, calendarSummary, calendarState, anchors } = context;

    const calendarStatusLabel = this.getCalendarStatusLabel(calendarState);

    this.container.innerHTML = `
      <div class="card guided-flow-card">
        <div class="guided-flow-card__header">
          <div>
            <h3 class="guided-flow-card__title">Guided Flow</h3>
            <p class="guided-flow-card__subtitle">Move through today in three calm passes.</p>
          </div>
          <span class="guided-flow-card__status ${calendarStatusLabel.className}">${calendarStatusLabel.label}</span>
        </div>

        <div class="guided-steps">
          ${steps
            .map(
              (step) => `
              <div class="guided-step guided-step--${step.status}">
                <div class="guided-step__header">
                  <span class="guided-step__emoji">${step.emoji}</span>
                  <div class="guided-step__titles">
                    <span class="guided-step__title">${step.title}</span>
                    <span class="guided-step__summary">${step.summary}</span>
                  </div>
                  <button class="guided-step__action" data-action="${step.id}" aria-label="Jump to ${step.title}">
                    Go
                  </button>
                </div>
                <p class="guided-step__hint">${step.hint}</p>
              </div>
            `
            )
            .join('')}
        </div>

        <div class="guided-feasibility guided-feasibility--${feasibility.status}">
          <div class="guided-feasibility__header">
            <h4>Daily Top Three check</h4>
            <span class="guided-feasibility__meta">${this.formatDuration(feasibility.totalDurationMinutes)} · ${
      feasibility.totalCapacity
    }% capacity</span>
          </div>
          <p class="guided-feasibility__message">${feasibility.message}</p>
          <div class="guided-feasibility__list">
            ${
              topThree.length > 0
                ? topThree
                    .map(
                      (task) => `
                        <div class="guided-task guided-task--${task.priority}">
                          <span class="guided-task__name">${task.text}</span>
                          <span class="guided-task__meta">${this.formatDuration(task.durationMinutes)} · ${
                        task.capacityPercent
                      }%</span>
                        </div>
                      `
                    )
                    .join('')
                : `<div class="guided-task guided-task--empty">Choose up to three focus anchors from Brain Space or your planner.</div>`
            }
          </div>
          <div class="guided-feasibility__calendar">
            <span>${calendarSummary.totalEvents} calendar block${calendarSummary.totalEvents === 1 ? '' : 's'} today</span>
            <span>${this.formatDuration(calendarSummary.busyMinutes)} claimed · ${this.formatDuration(
      calendarSummary.availableFocusMinutes
    )} focus time left</span>
          </div>
        </div>

        <div class="guided-anchors">
          <div class="guided-anchors__header">
            <h4>Values & Regulation Anchors</h4>
            <button class="guided-anchors__action" data-action="edit-anchors">Review</button>
          </div>
          <div class="guided-anchors__list">
            ${this.renderAnchorSummary(anchors)}
          </div>
        </div>
      </div>
    `;

    this.bindActions();
  }

  getCalendarStatusLabel(calendarState) {
    const status = calendarState.status || 'disabled';
    const labels = {
      disabled: { label: 'Calendar off', className: '' },
      pending_configuration: { label: 'Needs setup', className: 'is-warning' },
      initializing: { label: 'Connecting…', className: 'is-warning' },
      authorizing: { label: 'Authorizing…', className: 'is-warning' },
      exchanging: { label: 'Connecting…', className: 'is-warning' },
      ready: { label: 'Ready to sync', className: 'is-info' },
      connected: { label: 'Calendar active', className: 'is-connected' },
      error: { label: 'Calendar error', className: 'is-error' },
    };

    return labels[status] || labels.disabled;
  }

  bindActions() {
    const buttons = this.container.querySelectorAll('.guided-step__action');
    buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const action = event.currentTarget.getAttribute('data-action');
        this.handleAction(action);
      });
    });

    const editAnchorsBtn = this.container.querySelector('[data-action="edit-anchors"]');
    editAnchorsBtn?.addEventListener('click', () => this.openValuesAnchors());
  }

  handleAction(action) {
    if (action) {
      this.analytics?.track?.('guided_flow_action', { target: action });
    }
    switch (action) {
      case 'brain':
        this.focusBrainDump();
        break;
      case 'priorities':
        this.focusPriorities();
        break;
      case 'capacity':
        this.openCapacityPlanner();
        break;
      default:
        break;
    }
  }

  focusBrainDump() {
    const textarea = document.querySelector('.brain-dump-textarea');
    if (textarea) {
      textarea.focus();
      textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      document.getElementById('brain-dump-plus-container')?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  focusPriorities() {
    const highPrioritySection = document.querySelector('.priority-section.high-priority');
    if (highPrioritySection) {
      highPrioritySection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highPrioritySection.classList.add('guided-highlight');
      setTimeout(() => highPrioritySection.classList.remove('guided-highlight'), 2000);
      return;
    }

    document.getElementById('brain-dump-plus-container')?.scrollIntoView({ behavior: 'smooth' });
  }

  openCapacityPlanner() {
    this.analytics?.track?.('guided_flow_open_planner');
    if (typeof this.onOpenPlanner === 'function') {
      this.onOpenPlanner();
      return;
    }

    const plannerBtn = document.getElementById('enough-open-planner');
    if (plannerBtn) {
      plannerBtn.click();
    } else {
      document.getElementById('enough-capacity-container')?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  renderAnchorSummary(anchors) {
    const order = [
      { key: 'lifeVest', label: 'Life Vest', icon: '🛟' },
      { key: 'northStar', label: 'North Star', icon: '⭐' },
      { key: 'anchor', label: 'Anchor', icon: '⚓' },
      { key: 'buoy', label: 'Buoy', icon: '🪺' },
    ];

    const populated = order
      .map(({ key, label, icon }) => {
        const text = (anchors?.[key] || '').trim();
        if (!text) return null;
        return `
          <div class="guided-anchor">
            <span class="guided-anchor__icon">${icon}</span>
            <div class="guided-anchor__content">
              <span class="guided-anchor__label">${label}</span>
              <span class="guided-anchor__text">${this.truncateText(text, 140)}</span>
            </div>
          </div>
        `;
      })
      .filter(Boolean);

    if (populated.length === 0) {
      return `
        <div class="guided-anchor guided-anchor--empty">
          <span class="guided-anchor__icon">🧭</span>
          <div class="guided-anchor__content">
            <span class="guided-anchor__text">Capture your anchors so the dashboard can echo them back when you check-in.</span>
          </div>
        </div>
      `;
    }

    return populated.join('');
  }

  truncateText(text, limit = 140) {
    if (!text) return '';
    if (text.length <= limit) return text;
    return `${text.slice(0, limit - 1)}…`;
  }

  openValuesAnchors(anchorKey = 'lifeVest') {
    this.analytics?.track?.('guided_flow_anchor_open', { anchor: anchorKey });
    const valuesModule = window.lifeDashboard?.modules?.valuesAnchors || window.lifeValuesAnchors;
    if (valuesModule?.startQuickEdit) {
      valuesModule.startQuickEdit(anchorKey);
      return;
    }

    const anchorsSection = document.getElementById('values-anchors-container');
    if (anchorsSection) {
      anchorsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  reportContextMetrics(previous, next) {
    if (!next) return;

    const newStatus = next?.feasibility?.status;
    const prevStatus = previous?.feasibility?.status;
    if (newStatus && newStatus !== prevStatus) {
      this.analytics?.track?.('guided_flow_feasibility', { status: newStatus });
    }

    const newCount = next?.topThree?.length ?? 0;
    const prevCount = previous?.topThree?.length ?? 0;
    if (newCount !== prevCount) {
      this.analytics?.track?.('guided_flow_top_three_count', { count: newCount });
    }

    const newConflict = Boolean(next?.calendarSummary?.conflictWarning);
    const prevConflict = Boolean(previous?.calendarSummary?.conflictWarning);
    if (newConflict !== prevConflict) {
      this.analytics?.track?.('guided_flow_calendar_conflict', {
        status: newConflict ? 'warning' : 'clear',
      });
    }

    const newAnchors = this.countAnchors(next?.anchors);
    const prevAnchors = this.countAnchors(previous?.anchors);
    if (newAnchors !== prevAnchors) {
      this.analytics?.track?.('guided_flow_anchor_count', { count: newAnchors });
    }
  }

  countAnchors(anchors) {
    if (!anchors || typeof anchors !== 'object') return 0;
    const keys = ['lifeVest', 'northStar', 'anchor', 'buoy'];
    return keys.filter((key) => (anchors[key] || '').trim()).length;
  }

  formatDuration(minutes) {
    if (!Number.isFinite(minutes) || minutes <= 0) {
      return '0 min';
    }
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = minutes / 60;
    if (hours >= 4) {
      return `${hours.toFixed(1)} hrs`;
    }
    const wholeHours = Math.floor(hours);
    const remainderMinutes = Math.round((hours - wholeHours) * 60);
    if (remainderMinutes === 0) {
      return `${wholeHours} hr${wholeHours === 1 ? '' : 's'}`;
    }
    return `${wholeHours}h ${remainderMinutes}m`;
  }

  dispose() {
    this.unsubscribeFns.forEach((fn) => {
      try {
        fn();
      } catch (error) {
        console.warn('[GuidedDailyFlow] Failed to unsubscribe', error);
      }
    });
    this.unsubscribeFns = [];

    if (this.container) {
      this.container.innerHTML = '';
    }
  }
}
